import React, { useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useTranslation } from "contexts/Safra/TranslationContext";
import {
  FlightCard,
  TarifaSection,
  SelectButton,
  SegmentHeader,
  FlightInfo,
} from "./styles";
import { usaPontos } from "../../../utils";
import { formatCurrency } from "../../../utils/functions";

export const FlightListTile = ({
  data,
  onSelect,
  disabled: parentDisabled,
  onSegmentSelect,
  selectedOutboundId,
  selectedInboundId,
  isChoosing,
}) => {
  const { t } = useTranslation();
  const {
    airline = "LATAM",
    airlineLogo = "",
    outbound = {},
    inbound = {},
    pointsPerAdult = 0,
    pax = 0,
    tax = 0,
    totalPoints = 0,
    moneyPerAdult = 0,
    totalMoney = 0,
    taxMoney = 0,
  } = data || {};

  const isMoney = !usaPontos();

  const [showOutboundDetails, setShowOutboundDetails] = useState(false);
  const [showInboundDetails, setShowInboundDetails] = useState(false);
  const isRoundTrip = inbound && inbound.departure;
  const isOutboundSelected = selectedOutboundId === data.id;
  const isInboundSelected = selectedInboundId === data.id;

  const selectionIsComplete = isRoundTrip
    ? isOutboundSelected && isInboundSelected
    : isOutboundSelected;

  const isButtonDisabled = parentDisabled || !selectionIsComplete || isChoosing;

  const formatTime = (dateTime) => {
    if (!dateTime) return "00:00";
    const date = new Date(dateTime);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const formatSegmentDate = (dateTime) => {
    if (!dateTime) return "";
    const date = new Date(dateTime);
    const days = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const months = [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatInterval = (milliseconds) => {
    if (typeof milliseconds !== "number" || isNaN(milliseconds)) return "";
    const totalMinutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    let result = "";
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m`;
    return result.trim() || "0m";
  };

  const formatIataCode = (iataCode) => {
    const trimmedCode = iataCode ? String(iataCode).trim() : "";
    return trimmedCode.length > 0 && /^[a-zA-Z0-9]+$/.test(trimmedCode)
      ? `(${trimmedCode})`
      : "";
  };

  const amazonUrl = process.env.REACT_APP_SAFRA_BANNER_LINKS;

  const normalizeAirlineCode = (code) => String(code || "").trim().toUpperCase();

  const buildAirlineLogo = (code) => {
    const normalized = normalizeAirlineCode(code);
    return normalized ? `${amazonUrl}/media/aereo/${normalized}.png` : "";
  };

  const resolveLegCarrierCode = (leg) =>
    normalizeAirlineCode(
      leg?.carrierCode || leg?.companyCode || leg?.carrier || leg?.airlineCode,
    );

  const resolveLegOperatedBy = (leg) =>
    normalizeAirlineCode(leg?.operatedBy) || resolveLegCarrierCode(leg);

  const CARRIER_NAMES = {
    G3: "GOL",
    AD: "Azul",
    LA: "LATAM",
    JJ: "LATAM",
    AF: "Air France",
    KL: "KLM",
    WA: "KLM Cityhopper",
    BA: "British Airways",
    IB: "Iberia",
    TP: "TAP Air Portugal",
    AZ: "ITA Airways",
    UA: "United Airlines",
    AA: "American Airlines",
    DL: "Delta",
    AC: "Air Canada",
    LH: "Lufthansa",
    TK: "Turkish Airlines",
    LX: "Swiss",
    AV: "Avianca",
    EK: "Emirates",
    AM: "Aeroméxico",
    VS: "Virgin Atlantic",
    ET: "Ethiopian Airlines",
  };

  const resolveAirlineName = (code) => {
    const normalized = normalizeAirlineCode(code);
    return CARRIER_NAMES[normalized] || normalized;
  };

  const buildFlightLabel = (leg, legAirlineCode) => {
    const raw = String(leg?.flightNumber || "").trim();
    if (!raw) return legAirlineCode;
    const number = raw.replace(/^[A-Za-z][0-9A-Za-z]\s*/, "").trim();
    return number ? `${legAirlineCode} ${number}` : raw;
  };

  const renderSegment = (
    segment,
    label,
    typeIcon,
    showDetails,
    toggleDetails,
    rawFlightSegments,
    mainAirline,
  ) => {
    if (!segment || !segment.departure) return null;

    const isDirect = segment.stops === 0;
    const duration = segment.duration || "01h00";
    const stopsLabel = isDirect
      ? t("safra.components.flight_list_tile.direct")
      : t("safra.components.flight_list_tile.stops", { count: segment.stops });

    const carryOnAllowed =
      rawFlightSegments?.[0]?.flights?.[0]?.baggage?.carryOn?.allowance === "1";
    const dispatchedAllowed =
      rawFlightSegments?.[0]?.flights?.[0]?.baggage?.dispatched?.allowance ===
      "1";
    const segmentType = label.toLowerCase();

    const isChecked =
      segmentType === "ida" ? isOutboundSelected : isInboundSelected;

    const topAirlineCode =
      normalizeAirlineCode(rawFlightSegments?.[0]?.companyCode) ||
      normalizeAirlineCode(mainAirline?.substring(0, 2));

    return (
      <React.Fragment key={label}>
        <SegmentHeader>
          <div className="header-content">
            <img
              src={`${amazonUrl}/public/safra/img/${typeIcon}.png`}
              alt=""
              className="icon-aviao"
            />
            <span className="ida-label">{label === "Ida" ? t('safra.home.fields.departure') : t('safra.home.fields.return')}</span>
            <span className="ida-info">
              {formatSegmentDate(segment.departure.dateTime)}
            </span>
          </div>
        </SegmentHeader>

        <FlightInfo>
          <div className="row-main">
            <div className="radio-col">
              <label className="custom-ll-radio">
                <input
                  type="radio"
                  name={`journey-${segmentType}`}
                  id={`radio-${data.id}-${segmentType}`}
                  checked={isChecked}
                  onChange={() => onSegmentSelect(segmentType, data.id)}
                  aria-label={t('safra.components.flight_list_tile.select_flight_aria', {
                    label: label === "Ida" ? t('safra.home.fields.departure') : t('safra.home.fields.return'),
                    departure: segment.departure.city,
                    arrival: segment.arrival.city
                  })}
                />
                <img
                  className="unchecked"
                  src={`${amazonUrl}/public/safra/img/unchecked.png`}
                  alt={t('safra.components.flight_list_tile.not_selected')}
                />
                <img
                  className="checked"
                  src={`${amazonUrl}/public/safra/img/checked.png`}
                  alt={t('safra.components.flight_list_tile.selected')}
                />
              </label>
            </div>

            <div className="hora-col">
              <div className="voo-hora-box">
                <span className="sigla">{segment.departure.iataCode}</span>
                <span className="local">{segment.departure.city}</span>
                <span className="hora">
                  {formatTime(segment.departure.dateTime)}
                </span>
              </div>
            </div>

            <div className="timeline-col">
              <div className="timeline-box">
                <div className="txt-direto">
                  {stopsLabel}
                </div>
                <hr className="hr-timeline" />
                <div className="txt-duration">{duration}</div>
              </div>
            </div>

            <div className="hora-col">
              <div className="voo-hora-box">
                <span className="sigla">{segment.arrival.iataCode}</span>
                <span className="local">{segment.arrival.city}</span>
                <span className="hora">
                  {formatTime(segment.arrival.dateTime)}
                </span>
              </div>
            </div>

            <div className="info-col">
              <div
                className="btn_open_flight_info"
                role="button"
                tabIndex={0}
                aria-label={t('safra.components.flight_list_tile.show_details')}
                onClick={toggleDetails}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleDetails();
                  }
                }}
              >
                {showDetails ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
              </div>
            </div>
          </div>

          <div className="bagagem-row">
            <span className="label-bagagem">{t("safra.components.flight_list_tile.baggage")}:</span>
            <div className="icons-pilha">
              <img
                src={`${amazonUrl}/public/safra/img/luggage.svg`}
                alt={t("safra.components.flight_list_tile.hand_baggage")}
                className={!carryOnAllowed ? "opacity-04" : ""}
              />
              <img
                src={`${amazonUrl}/public/safra/img/baggage-claim.svg`}
                alt={t("safra.components.flight_list_tile.checked_baggage")}
                className={!dispatchedAllowed ? "opacity-04" : ""}
              />
            </div>
          </div>
        </FlightInfo>

        {showDetails && rawFlightSegments && rawFlightSegments[0]?.flights && (
          <div
            className="flight-details-section"
            style={{
              borderTop: "1px solid #eee",
              marginTop: "15px",
              paddingTop: "15px",
            }}
          >
            {rawFlightSegments[0].flights.map((leg, index, arr) => {
              const legCarrierCode = resolveLegCarrierCode(leg);
              const legOperatedBy = resolveLegOperatedBy(leg);
              const legLogoCode = legOperatedBy || topAirlineCode;
              const legFlightCode = legCarrierCode || topAirlineCode;
              const legLogo = buildAirlineLogo(legLogoCode);
              return (
              <React.Fragment key={leg.flightNumber + index}>
                <div
                  style={{
                    marginBottom: "15px",
                    padding: "10px",
                    border: "1px solid #f0f0f0",
                    borderRadius: "5px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      margin: "0 0 12px 0",
                    }}
                  >
                    <span>
                        {legLogo && (
                          <img
                            src={legLogo}
                            alt={legLogoCode}
                            style={{
                              height: "24px",
                              width: "auto",
                              objectFit: "contain",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#555" }}>
                          {t('safra.components.flight_list_tile.operated_by', { airline: resolveAirlineName(legOperatedBy) })}
                        </p>
                    </span>
                   
                    <p style={{ margin: 0 }}>
                      {t('safra.components.flight_list_tile.flight_number', { number: buildFlightLabel(leg, legFlightCode) })}
                    </p>
                  </div>
                 
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto 1fr",
                      alignItems: "center",
                      marginBottom: "12px",
                      gap: "8px",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", height: '100%', justifyContent: "flex-start" }}>
                      <span style={{ fontWeight: 600 }}>
                        {formatTime(leg.departureDate)} {formatIataCode(leg.departureAirport?.code)}
                      </span>
                      <span>{leg.departureAirport?.cityName}</span>
                    </div>
                    <span style={{ textAlign: "center", height: '100%' }}>→</span>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right", height: '100%', justifyContent: "flex-start" }}>
                      <span style={{ fontWeight: 600 }}>
                        {formatTime(leg.arrivalDate)} {formatIataCode(leg.arrivalAirport?.code)}
                      </span>
                      <span>{leg.arrivalAirport?.cityName}</span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "0",
                    }}
                  >
                    <p style={{ margin: "0", fontSize: "12px", color: "#555", fontWeight: 700 }}>
                    {t('safra.components.flight_list_tile.duration_segment')}{" "}
                    {formatInterval(
                      new Date(leg.arrivalDate).getTime() -
                        new Date(leg.departureDate).getTime(),
                    )}
                  </p>
                  {(() => {
                      const cabinRaw = (leg.cabin || '').toLowerCase();
                      const isExecutive = !cabinRaw.includes('economy');
                      const label = isExecutive ? 'Executiva' : 'Econômica';
                      const color = isExecutive ? '#92400E' : '#475569';
                      return (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: color,
                          border: `1px solid ${color}`,
                          borderRadius: '6px',
                          padding: '4px 8px',
                          whiteSpace: 'nowrap',
                        }}>
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {index < arr.length - 1 && (
                  <div
                    style={{
                      margin: "10px 0",
                      padding: "8px 10px",
                      backgroundColor: "#f9f9f9",
                      borderLeft: "3px solid #007bff",
                      fontSize: "13px",
                    }}
                  >
                    <p style={{ margin: "0" }}>
                      {t('safra.components.flight_list_tile.layover', {
                        duration: formatInterval(
                          new Date(arr[index + 1].departureDate).getTime() -
                            new Date(leg.arrivalDate).getTime(),
                        ),
                        city: leg.arrivalAirport.cityName
                      })}
                    </p>
                  </div>
                )}
              </React.Fragment>
              );
            })}
          </div>
        )}
      </React.Fragment>
    );
  };

  // Fallback for broken logos like JJ
  const safeLogo =
    airlineLogo ||
    `${amazonUrl}/media/aereo/${airline.substring(0, 2).toUpperCase()}.png`;

  return (
    <FlightCard>
      <TarifaSection>
        <div className="row">
          <div
            className="col-4"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div className="company-box">
              <img
                src={safeLogo}
                alt={airline}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
              <p className="company-name">{airline}</p>
            </div>
          </div>
          <div className="col-8 tarifa-col">
            <p className="tarifa-titulo">{t("safra.components.flight_list_tile.fare_per_adult")}</p>
            <div className="tarifa-valor-container">
              {isMoney ? (
                <span className="tarifa-valor">{formatCurrency(moneyPerAdult)}</span>
              ) : (
                <>
                  <span className="tarifa-valor">
                    {pointsPerAdult.toLocaleString("pt-BR")}
                  </span>
                  <span className="tarifa-pontos">{t("safra.components.flight_list_tile.points")}</span>
                </>
              )}
            </div>
            <span className="tarifa-desc">
              {t("safra.components.flight_list_tile.total_passengers", { pax: pax })} <br />
              {isMoney ? (
                <>
                  <strong>{formatCurrency(totalMoney)}</strong> | Taxas:{" "}
                  <strong>{formatCurrency(taxMoney)}</strong>
                </>
              ) : (
                <>
                  <strong>{totalPoints.toLocaleString("pt-BR")}</strong> {t("safra.components.flight_list_tile.points")} |
                  {t("safra.components.flight_list_tile.taxes")} <strong>{tax.toLocaleString("pt-BR")}</strong> {t("safra.components.flight_list_tile.points")}
                </>
              )}
            </span>
            <SelectButton
              onClick={() => onSelect(data)}
              aria-label={
                isChoosing
                  ? (isMoney ? "Comprando voo..." : t("safra.components.flight_list_tile.rescuing"))
                  : (isMoney ? `Comprar voo ${airline}` : t("safra.components.flight_list_tile.rescue_label", { airline, points: totalPoints }))
              }
              disabled={isButtonDisabled}
            >
              {isChoosing
                ? (isMoney ? "COMPRANDO..." : t("safra.components.flight_list_tile.rescuing"))
                : (isMoney ? "COMPRAR" : t("safra.components.flight_list_tile.rescue"))}
            </SelectButton>
          </div>
        </div>
      </TarifaSection>

      {renderSegment(
        outbound,
        "Ida",
        "Ico_Aviao_ida",
        showOutboundDetails,
        () => setShowOutboundDetails(!showOutboundDetails),
        data.raw.departureFlights,
        airline,
      )}
      {inbound &&
        inbound.departure &&
        renderSegment(
          inbound,
          "Volta",
          "Ico_aviao_volta",
          showInboundDetails,
          () => setShowInboundDetails(!showInboundDetails),
          data.raw.returnFlights,
          airline,
        )}
    </FlightCard>
  );
};
